import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { tasksAPI, inventoryAPI } from '../services/api';
import { Link } from 'react-router-dom';
import { 
  Heart, 
  CheckSquare, 
  ShoppingBag, 
  Trophy, 
  Clock, 
  Star,
  Gift,
  Target,
  Package
} from 'lucide-react';
import { toast } from 'react-toastify';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalTasks: 0,
    completedTasks: 0,
    pendingTasks: 0,
    totalInventoryItems: 0,
    availableItems: 0,
    usedItems: 0,
  });
  const [recentTasks, setRecentTasks] = useState([]);
  const [recentInventory, setRecentInventory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [tasksResponse, inventoryResponse] = await Promise.all([
        tasksAPI.getTasks(),
        inventoryAPI.getInventory(),
      ]);

      if (tasksResponse.data.success) {
        const tasks = tasksResponse.data.tasks;
        
        // Filter tasks to only include those assigned to the current user
        const myTasks = tasks.filter(task => task.assignedTo._id === user.id);
        
        setRecentTasks(tasks.slice(0, 5));
        setStats(prev => ({
          ...prev,
          totalTasks: myTasks.length,
          completedTasks: myTasks.filter(task => task.isCompleted).length,
          pendingTasks: myTasks.filter(task => !task.isCompleted).length,
        }));
      }

      if (inventoryResponse.data.success) {
        const inventory = inventoryResponse.data.inventory;
        setRecentInventory(inventory.slice(0, 5));
        setStats(prev => ({
          ...prev,
          totalInventoryItems: inventory.length,
          availableItems: inventory.filter(item => !item.isUsed).length,
          usedItems: inventory.filter(item => item.isUsed).length,
        }));
      }
    } catch (error) {
      toast.error('Error loading dashboard data! 😢');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, bgColor }) => (
    <div className={`card-cute ${bgColor} p-4`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-mobile-sm text-gray-600">{title}</p>
          <p className={`text-mobile-2xl font-bold ${color}`}>{value}</p>
        </div>
        <Icon className={`w-6 h-6 xs:w-8 xs:h-8 ${color}`} />
      </div>
    </div>
  );

  const TaskCard = ({ task }) => (
    <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-pookie-pink-100 active:scale-95 transition-transform">
      <span className="text-lg xs:text-xl">{task.emoji}</span>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-mobile-sm truncate">{task.title}</p>
        <p className="text-2xs text-gray-600">
          {task.isCompleted ? 'Completed' : 'Pending'} • {task.pookiePoints} points
        </p>
      </div>
      <div className={`w-3 h-3 rounded-full ${task.isCompleted ? 'bg-green-500' : 'bg-yellow-500'}`} />
    </div>
  );

  const InventoryCard = ({ item }) => (
    <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-pookie-pink-100 active:scale-95 transition-transform">
      <span className="text-lg xs:text-xl">{item.itemEmoji}</span>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-mobile-sm truncate">{item.itemName}</p>
        <p className="text-2xs text-gray-600">
          {item.isUsed ? 'Used' : 'Available'} • {item.pookiePointsSpent} points
        </p>
      </div>
      <div className={`w-3 h-3 rounded-full ${item.isUsed ? 'bg-gray-500' : 'bg-green-500'}`} />
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="loading-spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="space-mobile-y pb-20 lg:pb-0">
      {/* Welcome Section */}
      <div className="text-center px-mobile">
        <h1 className="text-mobile-3xl font-bold text-gradient text-cute mb-4">
          Welcome back, {user?.displayName}! 💕
        </h1>
        <p className="text-mobile-lg text-gray-600">
          You have {user?.pookiePoints || 0} Pookie Points to spend!
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-4">
        <StatCard
          title="Total Tasks"
          value={stats.totalTasks}
          icon={CheckSquare}
          color="text-pookie-purple-600"
          bgColor="bg-pookie-purple-50"
        />
        <StatCard
          title="Completed"
          value={stats.completedTasks}
          icon={Trophy}
          color="text-green-600"
          bgColor="bg-green-50"
        />
        <StatCard
          title="Available"
          value={stats.availableItems}
          icon={Package}
          color="text-pookie-pink-600"
          bgColor="bg-pookie-pink-50"
        />
        <StatCard
          title="Used Items"
          value={stats.usedItems}
          icon={Gift}
          color="text-gray-600"
          bgColor="bg-gray-50"
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 gap-4 px-4">
        <Link to="/tasks" className="card-cute active:scale-95 transition-transform">
          <div className="text-center p-4">
            <CheckSquare className="w-10 h-10 xs:w-12 xs:h-12 mx-auto mb-3 text-pookie-purple-500" />
            <h3 className="text-mobile-lg font-semibold mb-2">Manage Tasks</h3>
            <p className="text-mobile-sm text-gray-600">Create and complete tasks</p>
          </div>
        </Link>
        
        <Link to="/store" className="card-cute active:scale-95 transition-transform">
          <div className="text-center p-4">
            <ShoppingBag className="w-10 h-10 xs:w-12 xs:h-12 mx-auto mb-3 text-pookie-pink-500" />
            <h3 className="text-mobile-lg font-semibold mb-2">Pookie Store</h3>
            <p className="text-mobile-sm text-gray-600">Shop & manage inventory</p>
          </div>
        </Link>
        
        <div className="card-cute p-4 xs:col-span-2 lg:col-span-1">
          <div className="text-center">
            <Gift className="w-10 h-10 xs:w-12 xs:h-12 mx-auto mb-3 text-yellow-500" />
            <h3 className="text-mobile-lg font-semibold mb-2">Your Points</h3>
            <div className="points-display mx-auto text-mobile-base">
              💎 {user?.pookiePoints || 0}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 px-4">
        {/* Recent Tasks */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-mobile-xl font-semibold text-gray-800">Recent Tasks</h2>
            <Link to="/tasks" className="text-pookie-pink-600 hover:text-pookie-pink-700 text-mobile-sm font-medium">
              View all
            </Link>
          </div>
          <div className="space-y-3">
            {recentTasks.length > 0 ? (
              recentTasks.map((task) => (
                <TaskCard key={task._id} task={task} />
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Target className="w-10 h-10 xs:w-12 xs:h-12 mx-auto mb-2 opacity-50" />
                <p className="text-mobile-sm">No tasks yet! Create your first task.</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Inventory */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-mobile-xl font-semibold text-gray-800">Recent Inventory</h2>
            <Link to="/store" className="text-pookie-pink-600 hover:text-pookie-pink-700 text-mobile-sm font-medium">
              View inventory
            </Link>
          </div>
          <div className="space-y-3">
            {recentInventory.length > 0 ? (
              recentInventory.map((item) => (
                <InventoryCard key={item._id} item={item} />
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Package className="w-10 h-10 xs:w-12 xs:h-12 mx-auto mb-2 opacity-50" />
                <p className="text-mobile-sm">No items yet! Purchase from the store.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Motivational Message */}
      <div className="text-center p-mobile bg-gradient-to-r from-pookie-pink-100 to-pookie-purple-100 rounded-2xl mx-4">
        <Heart className="w-8 h-8 mx-auto mb-2 text-pookie-pink-500 animate-pulse-heart" />
        <p className="text-mobile-lg font-semibold text-gray-800">
          Keep being amazing! Every task completed brings you closer to your rewards! 💕
        </p>
      </div>
    </div>
  );
};

export default Dashboard; 